<div class="flex flex-column gap-3 h-full">
  <toolbar class="w-full" title="Journaux techniques" />

  <!-- Services -->
  <div class="log-services">
    <button type="button" class="log-service" [class.log-service--active]="service === ALL" (click)="selectService(ALL)">
      <i class="pi pi-th-large"></i>
      <span class="log-service__label">Tous les services</span>
    </button>
    @for (source of sources; track source.service) {
      <button type="button" class="log-service" [class.log-service--active]="service === source.service"
              pTooltip="Aucun fichier de logs du jour pour ce service" [tooltipDisabled]="source.available"
              tooltipPosition="bottom" (click)="selectService(source.service)">
        <span class="log-service__dot" [class.log-service__dot--on]="source.available"></span>
        <span class="log-service__label">{{ source.label }}</span>
        @if (source.lastModified) {
          <span class="log-service__meta">{{ source.lastModified | date:'HH:mm:ss' }}</span>
        }
      </button>
    }
  </div>

  <!-- Filtres -->
  <div class="log-filters">
    <p-select [options]="dateOptions" [(ngModel)]="date" optionLabel="label" optionValue="value"
                (onChange)="onDateChange()" styleClass="log-filters__date" />

    <div class="log-levels">
      @for (level of levelOptions; track level) {
        <button type="button" class="log-level log-level--{{ level | lowercase }}"
                [class.log-level--selected]="levels.includes(level)" (click)="toggleLevel(level)">
          {{ level }}
        </button>
      }
    </div>

    <span class="p-input-icon-left log-filters__search">
      <i class="pi pi-search"></i>
      <input pInputText type="text" placeholder="Rechercher (message, classe, stacktrace)"
             [(ngModel)]="query" (ngModelChange)="onTextChange()" />
    </span>

    <span class="p-input-icon-left log-filters__user">
      <i class="pi pi-user"></i>
      <input pInputText type="text" placeholder="Utilisateur" [(ngModel)]="user" (ngModelChange)="onTextChange()" />
    </span>

    <p-select [options]="limitOptions" [(ngModel)]="limit" optionLabel="label" optionValue="value" (onChange)="load()" />

    <div class="log-filters__actions">
      <label class="log-filters__switch" pTooltip="Disponible uniquement sur le jour en cours" [tooltipDisabled]="isToday">
        <p-toggleswitch [ngModel]="autoRefresh" (ngModelChange)="setAutoRefresh($event)" [disabled]="!isToday" />
        <span>Auto (10 s)</span>
      </label>
      <p-button icon="pi pi-refresh" [rounded]="true" [text]="true" pTooltip="Rafraîchir" (onClick)="refresh()" />
      <p-button icon="pi pi-download" [rounded]="true" [text]="true" [disabled]="service === ALL"
                [pTooltip]="service === ALL ? 'Choisir un service pour télécharger son fichier' : 'Télécharger le fichier brut'"
                (onClick)="download()" />
    </div>
  </div>

  <!-- Synthèse -->
  @if (page) {
    <div class="log-summary">
      <span><strong>{{ rows.length }}</strong> entrée(s)</span>
      <button type="button" class="log-summary__count log-summary__count--error" (click)="focusLevel('ERROR')"
              pTooltip="N'afficher que les erreurs">
        <i class="pi pi-times-circle"></i> {{ errorCount }} erreur(s)
      </button>
      <button type="button" class="log-summary__count log-summary__count--warn" (click)="focusLevel('WARN')"
              pTooltip="N'afficher que les avertissements">
        <i class="pi pi-exclamation-triangle"></i> {{ warnCount }} avertissement(s)
      </button>
      <span class="log-summary__muted">{{ page.scannedLines | number }} ligne(s) lue(s)</span>
      @if (rows.length >= limit) {
        <span class="log-summary__muted">· limite atteinte, les entrées plus anciennes ne sont pas affichées</span>
      }
    </div>
    @if (page.truncated) {
      <div class="log-banner">
        <i class="pi pi-info-circle"></i>
        Fichier volumineux : seule la fin du fichier a été parcourue. Affinez les filtres ou téléchargez le fichier complet.
      </div>
    }
  }

  @if (loading) {
    <p-progressBar mode="indeterminate" styleClass="log-progress" />
  }

  @if (errorMessage) {
    <div class="log-empty">
      <i class="pi pi-folder-open"></i>
      <span>{{ errorMessage }}</span>
    </div>
  } @else {
    <div class="log-table">
      <p-table [value]="rows" dataKey="key" [expandedRowKeys]="expandedRows" [paginator]="rows.length > 100" [rows]="100"
               [scrollable]="true" scrollHeight="flex" styleClass="p-datatable-sm">
        <ng-template pTemplate="header">
          <tr>
            <th class="log-col-toggle"></th>
            <th class="log-col-time">Horodatage</th>
            <th class="log-col-level">Niveau</th>
            @if (service === ALL) {
              <th class="log-col-service">Service</th>
            }
            <th class="log-col-user">Utilisateur</th>
            <th class="log-col-logger">Classe</th>
            <th>Message</th>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-row let-expanded="expanded">
          <tr class="log-row log-row--{{ row.level | lowercase }}" [pRowToggler]="row">
            <td class="log-col-toggle">
              <i class="pi" [class.pi-chevron-down]="expanded" [class.pi-chevron-right]="!expanded"></i>
            </td>
            <td class="log-col-time log-mono">{{ row.timestamp | date:'dd/MM HH:mm:ss.SSS' }}</td>
            <td class="log-col-level"><span class="log-badge log-badge--{{ row.level | lowercase }}">{{ row.level }}</span></td>
            @if (service === ALL) {
              <td class="log-col-service">{{ sourceLabel(row.service) }}</td>
            }
            <td class="log-col-user">{{ row.username ?? '—' }}</td>
            <td class="log-col-logger log-mono" [pTooltip]="row.logger" tooltipPosition="top">{{ shortLogger(row.logger) }}</td>
            <td class="log-col-message log-mono">
              {{ row.message }}
              @if (row.stackTrace) {
                <span class="log-stack-hint" pTooltip="Contient une stacktrace ou un message sur plusieurs lignes">
                  <i class="pi pi-align-left"></i>
                </span>
              }
            </td>
          </tr>
        </ng-template>

        <ng-template pTemplate="rowexpansion" let-row>
          <tr class="log-detail-row">
            <td [attr.colspan]="service === ALL ? 7 : 6">
              <div class="log-detail">
                <div class="log-detail__meta">
                  <span><strong>Service :</strong> {{ sourceLabel(row.service) }}</span>
                  <span><strong>Classe :</strong> <span class="log-mono">{{ row.logger }}</span></span>
                  <span><strong>Thread :</strong> <span class="log-mono">{{ row.thread }}</span></span>
                  <span><strong>Utilisateur :</strong> {{ row.username ?? '—' }}</span>
                  <span><strong>IP :</strong> {{ row.ipAddress ?? '—' }}</span>
                  <p-button icon="pi pi-copy" label="Copier" size="small" [text]="true" (onClick)="copy(row)" />
                </div>
                <pre class="log-detail__message">{{ row.message }}</pre>
                @if (row.stackTrace) {
                  <pre class="log-detail__stack">{{ row.stackTrace }}</pre>
                }
              </div>
            </td>
          </tr>
        </ng-template>

        <ng-template pTemplate="emptymessage">
          <tr>
            <td [attr.colspan]="service === ALL ? 7 : 6">
              @if (!loading) {
                <div class="log-empty">
                  <i class="pi pi-inbox"></i>
                  <span>Aucun log ne correspond à ces filtres.</span>
                </div>
              }
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  }
</div>
